/**
 * Question Service
 * Handles question operations (fetch, seed, verify, initialize)
 */

import { CHATBOT_API_BASE_URL, MAIN_API_BASE_URL, isValidObjectId } from '../utils/apiConfig';
import { getAccessToken, createHeaders } from '../utils/authHelper';
import { chatService } from './chatService';

export const questionService = {
    /**
     * Fetch question data from Main Backend
     * @param {string} questionId - Question ID
     * @returns {Promise<Object|null>} Question data or null
     */
    async fetchQuestionData(questionId) {
        try {
            const token = getAccessToken();

            // Try various possible endpoints
            const possibleEndpoints = [
                `/questions/${questionId}`,
                `/api/questions/${questionId}`,
                `/questions/practice/${questionId}`,
                `/api/question/${questionId}`,
                `/question/${questionId}`,
            ];

            let lastError;

            for (const endpoint of possibleEndpoints) {
                try {
                    const headers = {
                        "Content-Type": "application/json",
                    };

                    // Add auth header if token available
                    if (token) {
                        headers["Authorization"] = `Bearer ${token}`;
                    }

                    const response = await fetch(
                        `${MAIN_API_BASE_URL}${endpoint}`,
                        {
                            method: "GET",
                            headers: headers,
                        }
                    );

                    if (response.ok) {
                        const questionData = await response.json();
                        return questionData;
                    }

                    lastError = `${endpoint}: HTTP ${response.status}`;
                    console.warn(`❌ ${lastError}`);
                } catch (error) {
                    lastError = `${endpoint}: ${error.message}`;
                    console.warn(`❌ ${lastError}`);
                }
            }

            console.warn(
                `⚠️ All question endpoints failed. Last: ${lastError}`
            );
            return null;
        } catch (error) {
            console.error("Error fetching question data:", error);
            return null;
        }
    },

    /**
     * Seed a question by ID into Milvus with retry logic
     * @param {string} questionId - Question ID
     * @param {number} retryCount - Number of retries
     * @returns {Promise<Object>} Seed result
     */
    async seedQuestionById(questionId, retryCount = 3) {
        console.log(`🌱 Attempting to seed question: ${questionId}`);
        try {
            const response = await fetch(
                `${CHATBOT_API_BASE_URL}/api/seed-question-by-id/${questionId}`,
                {
                    method: "POST",
                    headers: createHeaders(),
                }
            );

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(
                    `Seeding failed: HTTP ${response.status} - ${errorText}`
                );
            }

            const result = await response.json();
            return result;
        } catch (error) {
            console.error("❌ Error seeding question:", error);

            // Retry if attempts remaining
            if (retryCount > 0) {
                await new Promise((resolve) =>
                    setTimeout(resolve, 1000)
                );
                return this.seedQuestionById(
                    questionId,
                    retryCount - 1
                );
            }

            throw error;
        }
    },

    /**
     * Verify that current question has been seeded correctly
     * @returns {Promise<boolean>} Whether verification was successful
     */
    async verifyCurrentQuestion() {
        try {
            const response = await fetch(
                `${CHATBOT_API_BASE_URL}/api/current-question`,
                {
                    headers: createHeaders(),
                }
            );
            if (response.ok) {
                const data = await response.json();
                if (data.exists) {
                    return true;
                } else {
                    console.warn(
                        "⚠️ Current question not found after seeding"
                    );
                    return false;
                }
            }
            return false;
        } catch (error) {
            console.error(
                "❌ Error verifying current question:",
                error
            );
            return false;
        }
    },

    /**
     * Initialize chatbot for a specific question
     * @param {string} questionId - Question ID
     * @param {string} modelChoice - OpenAI model to use
     * @returns {Promise<Object>} Initialization result
     */
    async initializeForQuestion(
        questionId,
        modelChoice = "gpt-4o-mini"
    ) {
        try {
            if (!questionId) {
                return {
                    success: false,
                    error: "Question ID is required",
                };
            }

            if (!isValidObjectId(questionId)) {
                console.warn(`⚠️ Invalid questionId format: ${questionId}. Expected 24-character hex string.`);
                return {
                    success: false,
                    error: `Invalid question ID format: ${questionId}`
                };
            }

            // 1. Try to seed the question first
            let seedResult = null;
            let seedWarning = null;

            try {
                seedResult = await this.seedQuestionById(questionId);
            } catch (seedError) {
                seedWarning = `Failed to seed question: ${seedError.message}`;
                console.warn("⚠️", seedWarning);
            }

            // 2. Try to get question data
            let questionData = null;
            let questionWarning = null;

            try {
                questionData = await this.fetchQuestionData(questionId);
                if (!questionData) {
                    questionWarning =
                        "Could not fetch question data - may not exist or need authentication";
                    console.warn("⚠️", questionWarning);
                }
            } catch (error) {
                questionWarning = `Could not fetch question data: ${error.message}`;
                console.warn("⚠️", questionWarning);
            }

            // 3. Initialize chatbot
            let chatbotResponse = null;
            let chatbotWarning = null;

            try {
                const initMessage = questionData
                    ? `I have a SAT question to work on. Question: "${questionData.question ||
                    questionData.questionText ||
                    "Question content not available"
                    }". Please help me understand this question.`
                    : `I'm working on SAT question ID: ${questionId}. Please help me with any questions about SAT problems.`;

                chatbotResponse = await chatService.sendMessage(
                    initMessage,
                    questionId,
                    modelChoice
                );
            } catch (chatError) {
                chatbotWarning = `Chatbot initialization failed: ${chatError.message}`;
                console.warn("⚠️", chatbotWarning);
            }

            // 4. Determine success status
            const hasData = questionData !== null;
            const hasChatbot = chatbotResponse !== null;
            const hasSeeded = seedResult !== null;

            return {
                success: hasData || hasChatbot || hasSeeded,
                questionData: questionData,
                chatbotResponse: chatbotResponse,
                seedResult: seedResult,
                questionId: questionId,
                warnings: [
                    seedWarning,
                    questionWarning,
                    chatbotWarning,
                ].filter(Boolean),
                status: {
                    questionDataLoaded: hasData,
                    chatbotReady: hasChatbot,
                    questionSeeded: hasSeeded,
                    authenticationRequired:
                        !hasData &&
                        questionWarning?.includes("authentication"),
                },
            };
        } catch (error) {
            console.error("❌ Error initializing chatbot:", error);
            return {
                success: false,
                error: error.message,
                questionId: questionId,
            };
        }
    },
};
